<template>
  <basic-page title="Kartta" fluid>
    <div class="map-header">
      <!-- Filter Chips -->
      <div class="map-filters-container">
        <v-btn
          icon
          small
          @click="showFilters = !showFilters"
          class="mr-2"
          :color="activeFilters.length < 6 ? 'primary' : 'grey'"
        >
          <v-icon>mdi-filter-variant</v-icon>
        </v-btn>
        <v-expand-transition>
          <div class="map-filters" v-show="showFilters">
            <v-chip-group v-model="activeFilters" multiple column>
              <v-chip
                filter
                outlined
                small
                value="fire_hydrant"
                active-class="primary--text"
                >Paloposti</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="water_station"
                active-class="light-blue--text"
                >Vesiasema</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="tap_water"
                active-class="teal--text"
                >Vesijohtovesi</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="natural_water"
                active-class="cyan--text"
                >Luonnonvedenottopaikka</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="hazard"
                active-class="error--text"
                >Vaarakohteet</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="meeting"
                active-class="success--text"
                >Kokoontumispaikat</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="boat_launch"
                active-class="cyan--text"
                >Veneenlaskupaikat</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="alarm"
                active-class="red--text"
                v-if="latestAlarmMarker"
                >Viimeisin hälytys</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="local"
                active-class="purple--text"
                v-if="localMarkers.length > 0 || sharedMarkers.length > 0"
                >Omat/Jaetut</v-chip
              >
              <v-chip
                filter
                outlined
                small
                value="other"
                active-class="grey--text"
                >Muut</v-chip
              >
            </v-chip-group>
          </div>
        </v-expand-transition>
      </div>

      <!-- Search Bar -->
      <div class="map-search">
        <div class="d-none d-md-flex align-center">
          <v-btn-toggle
            v-model="searchMode"
            mandatory
            dense
            class="mr-2 white"
            style="height: 40px; border: 1px solid rgba(0, 0, 0, 0.12)"
          >
            <v-btn value="address" small title="Hae osoite">
              <v-icon small>mdi-map-marker</v-icon>
            </v-btn>
            <v-btn value="coordinates" small title="Hae koordinaatit">
              <v-icon small>mdi-crosshairs-gps</v-icon>
            </v-btn>
          </v-btn-toggle>

          <v-text-field
            v-model="searchQuery"
            :label="searchLabel"
            @keyup.enter="performSearch"
            prepend-inner-icon="mdi-magnify"
            clearable
            dense
            :loading="searching"
            style="min-width: 250px"
          ></v-text-field>
          <v-btn @click="performSearch" :loading="searching" small class="ml-2"
            >Hae</v-btn
          >
        </div>

        <v-btn class="d-md-none" icon small @click="searchDialog = true">
          <v-icon>mdi-magnify</v-icon>
        </v-btn>

        <!-- Super Admin: Toggle Public Edit -->
        <v-btn
          v-if="$isSuperAdmin"
          icon
          small
          :color="tempEditAccess ? 'red' : 'grey'"
          class="ml-2"
          @click="togglePublicEdit"
          title="Hallitse julkista muokkausta"
        >
          <v-icon>{{ tempEditAccess ? "mdi-lock-open" : "mdi-lock" }}</v-icon>
        </v-btn>
      </div>
    </div>

    <div class="map-container">
      <div class="map-crosshair">
        <v-icon color="black">mdi-plus</v-icon>
      </div>
      <l-map
        ref="map"
        :zoom="zoom"
        :center="center"
        @click="onMapClick"
        @mousemove="onMouseMove"
        @moveend="onMapMoveEnd"
        style="height: 100%; width: 100%"
      >
        <l-control-layers position="topright"></l-control-layers>

        <!-- Base Layers -->
        <l-tile-layer
          v-for="tileProvider in tileProviders"
          :key="tileProvider.name"
          :name="tileProvider.name"
          :visible="tileProvider.visible"
          :url="tileProvider.url"
          :attribution="tileProvider.attribution"
          layer-type="base"
        />

        <!-- Markers -->
        <l-marker
          v-for="marker in filteredMarkers"
          :key="marker.id"
          :lat-lng="[marker.lat, marker.lng]"
          :icon="getMarkerIcon(marker)"
        >
          <l-popup>
            <div class="text-center">
              <h3 class="subtitle-1 font-weight-bold">{{ marker.name }}</h3>

              <img
                v-if="marker.imageUrl"
                :src="marker.imageUrl"
                style="
                  max-width: 200px;
                  max-height: 150px;
                  object-fit: contain;
                  display: block;
                  margin: 0 auto 8px auto;
                  cursor: pointer;
                "
                @click="openImage(marker.imageUrl)"
              />
              <v-btn
                v-if="marker.imageUrl"
                x-small
                text
                color="primary"
                class="mb-2"
                @click="openImage(marker.imageUrl)"
              >
                <v-icon x-small left>mdi-fullscreen</v-icon> Avaa kuva
              </v-btn>
              <p class="caption mb-2">{{ marker.description }}</p>
              <v-chip
                x-small
                class="mb-2"
                :color="getTypeColor(marker.type)"
                dark
                >{{ getTypeLabel(marker.type) }}</v-chip
              >
              <br />
              <v-btn
                x-small
                color="primary"
                :href="`https://www.google.com/maps/dir/?api=1&destination=${marker.lat},${marker.lng}`"
                target="_blank"
                class="mr-2"
              >
                <v-icon x-small left>mdi-navigation</v-icon> Navigoi
              </v-btn>
              <v-btn
                v-if="editMode || marker.isLocal"
                x-small
                color="secondary"
                icon
                class="mr-2"
                @click="openEditDialog(marker)"
              >
                <v-icon x-small>mdi-pencil</v-icon>
              </v-btn>
              <v-btn
                v-if="editMode || marker.isLocal"
                x-small
                color="error"
                icon
                @click="deleteMarkerConfirm(marker)"
              >
                <v-icon x-small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </l-popup>
        </l-marker>

        <!-- Latest Alarm Marker -->
        <l-marker
          v-if="latestAlarmMarker && activeFilters.includes('alarm')"
          :lat-lng="[latestAlarmMarker.lat, latestAlarmMarker.lng]"
          :icon="getMarkerIcon('alarm')"
        >
          <l-popup>
            <div class="text-center">
              <h3 class="subtitle-1 font-weight-bold error--text">
                {{ latestAlarmMarker.name }}
              </h3>
              <div
                class="caption font-weight-bold mb-1"
                v-if="latestAlarmMarker.date"
              >
                <v-icon x-small>mdi-clock-outline</v-icon>
                {{ formatDate(latestAlarmMarker.date) }}
                {{ latestAlarmMarker.time }}
              </div>
              <p class="caption mb-2">
                {{ latestAlarmMarker.description }}
              </p>
              <v-chip x-small class="mb-2" color="red" dark>Hälytys</v-chip>
              <br />
              <v-btn
                x-small
                color="primary"
                :href="`https://www.google.com/maps/dir/?api=1&destination=${latestAlarmMarker.lat},${latestAlarmMarker.lng}`"
                target="_blank"
              >
                <v-icon x-small left>mdi-navigation</v-icon> Navigoi
              </v-btn>
            </div>
          </l-popup>
        </l-marker>

        <!-- User Location Marker -->
        <l-circle
          v-if="userLocation && userAccuracy"
          :lat-lng="userLocation"
          :radius="userAccuracy"
          color="#4285F4"
          :weight="1"
          fill-color="#4285F4"
          :fill-opacity="0.15"
        />
        <l-marker v-if="userLocation" :lat-lng="userLocation">
          <l-icon
            :icon-size="[18, 18]"
            :icon-anchor="[9, 9]"
            class-name="user-location-dot"
          >
            <div class="user-dot"></div>
          </l-icon>
          <l-popup
            >Olet tässä (tarkkuus: {{ Math.round(userAccuracy) }}m)</l-popup
          >
        </l-marker>

        <!-- Temporary Marker (when adding) -->
        <l-marker
          v-if="dialog && newMarker.lat && newMarker.lng"
          :lat-lng="[newMarker.lat, newMarker.lng]"
          :icon="getMarkerIcon(newMarker)"
          :draggable="true"
          @update:latLng="updateTempMarkerPosition"
        />

        <!-- Search Marker -->
        <l-marker
          v-if="searchMarker"
          :lat-lng="[searchMarker.lat, searchMarker.lng]"
          :icon="getMarkerIcon('search')"
        >
          <l-popup>
            <div class="text-center">
              <h3 class="subtitle-1 font-weight-bold">
                {{ searchMarker.address }}
              </h3>
              <v-btn
                x-small
                color="primary"
                :href="`https://www.google.com/maps/dir/?api=1&destination=${searchMarker.lat},${searchMarker.lng}`"
                target="_blank"
              >
                <v-icon x-small left>mdi-navigation</v-icon> Navigoi
              </v-btn>
            </div>
          </l-popup>
        </l-marker>

        <!-- Temporary Click Marker -->
        <l-marker
          v-if="tempClickMarker"
          :lat-lng="[tempClickMarker.lat, tempClickMarker.lng]"
          :icon="getMarkerIcon('other')"
        >
          <l-popup>
            <div class="text-center">
              <h3 class="subtitle-1 font-weight-bold">Valittu sijainti</h3>
              <div class="caption">
                WGS84: {{ tempClickMarker.lat.toFixed(5) }},
                {{ tempClickMarker.lng.toFixed(5) }}
              </div>
              <div class="caption">
                WGS84 (DM): {{ formatToDm(tempClickMarker.lat) }} N,
                {{ formatToDm(tempClickMarker.lng) }} E
              </div>
              <div class="caption mb-2">
                ETRS-TM35FIN:
                {{
                  getEtrsCoordinates(tempClickMarker.lat, tempClickMarker.lng).n
                }}
                N,
                {{
                  getEtrsCoordinates(tempClickMarker.lat, tempClickMarker.lng).e
                }}
                E
              </div>
              <v-btn
                x-small
                color="primary"
                @click="
                  copyCoordinates(tempClickMarker.lat, tempClickMarker.lng)
                "
                class="mr-2"
              >
                <v-icon x-small left>mdi-content-copy</v-icon> Kopioi
              </v-btn>
              <v-btn
                x-small
                color="purple"
                class="white--text mr-2"
                @click="openAddLocalDialog(tempClickMarker)"
              >
                <v-icon x-small left>mdi-content-save</v-icon> Tallenna (oma)
              </v-btn>
              <v-btn
                x-small
                color="secondary"
                :href="`https://www.google.com/maps/search/?api=1&query=${tempClickMarker.lat},${tempClickMarker.lng}`"
                target="_blank"
              >
                <v-icon x-small left>mdi-google-maps</v-icon> Google Maps
              </v-btn>
            </div>
          </l-popup>
        </l-marker>

        <!-- Weather Marker -->
        <l-marker
          v-if="weatherMarker && !measureMode"
          :lat-lng="[weatherMarker.lat, weatherMarker.lng]"
          :icon="getWindMarkerIcon(weatherMarker.rotation)"
          :z-index-offset="-1000"
        >
          <l-popup>
            <div class="text-center">
              <strong>Tuuli</strong><br />
              {{ weather.windSpeed }} m/s
              <span v-if="weather.windGust" class="grey--text"
                >({{ weather.windGust }} m/s)</span
              ><br />
              Suunta: {{ weather.windDirection }}°
            </div>
          </l-popup>
        </l-marker>

        <!-- Wind Grid Markers -->
        <template v-if="!measureMode">
          <l-marker
            v-for="(marker, index) in windGridMarkers"
            :key="`wind-grid-${index}`"
            :lat-lng="[marker.lat, marker.lng]"
            :icon="getWindMarkerIcon(marker.rotation)"
            :z-index-offset="-1000"
          >
            <l-popup>
              <div class="text-center">
                <strong>Tuuli</strong><br />
                {{ marker.windSpeed }} m/s
                <span v-if="marker.windGust" class="grey--text"
                  >({{ marker.windGust }} m/s)</span
                ><br />
                Suunta: {{ marker.windDirection }}°
              </div>
            </l-popup>
          </l-marker>
        </template>

        <!-- Measurement Layers -->
        <l-polyline
          v-if="measureMode && measurePoints.length > 1"
          :lat-lngs="measurePoints"
          color="blue"
        />
        <l-polygon
          v-if="measureMode && measurePoints.length > 2"
          :lat-lngs="measurePoints"
          color="blue"
          :fill="true"
          :fill-opacity="0.2"
        />
        <template v-if="measureMode">
          <l-circle-marker
            v-for="(point, index) in measurePoints"
            :key="`measure-point-${index}-${showMeasureLabels}`"
            :lat-lng="point"
            :radius="5"
            color="blue"
            fill-color="blue"
            :fill-opacity="1"
          >
            <l-tooltip
              v-if="showMeasureLabels"
              :content="formatPoint(point)"
              :options="{
                permanent: true,
                direction: index % 2 === 0 ? 'top' : 'bottom',
                offset: [0, index % 2 === 0 ? -5 : 5],
              }"
            />
          </l-circle-marker>
        </template>
      </l-map>

      <!-- Floating Controls -->
      <div class="map-controls">
        <!-- Share Menu -->
        <v-menu offset-y left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              fab
              small
              color="white"
              class="mb-2"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon color="primary">mdi-share-variant</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="copyAppLink">
              <v-list-item-icon><v-icon>mdi-link</v-icon></v-list-item-icon>
              <v-list-item-title>Kopioi linkki</v-list-item-title>
            </v-list-item>
            <v-list-item @click="openGoogleMaps">
              <v-list-item-icon
                ><v-icon>mdi-google-maps</v-icon></v-list-item-icon
              >
              <v-list-item-title>Avaa Google Maps</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-btn
          fab
          small
          :color="watchId ? 'primary' : 'white'"
          class="mb-2"
          @click="locateUser"
          :loading="locating"
        >
          <v-icon :color="watchId ? 'white' : 'primary'"
            >mdi-crosshairs-gps</v-icon
          >
        </v-btn>

        <!-- Wind Grid Toggle -->
        <v-btn
          fab
          small
          color="white"
          class="mb-2"
          @click="fetchWindGrid"
          :loading="loadingWindGrid"
        >
          <v-icon color="primary">mdi-weather-windy</v-icon>
        </v-btn>

        <!-- Coordinates Toggle -->
        <v-btn
          fab
          small
          :color="showCoordinates ? 'secondary' : 'white'"
          class="mb-2"
          @click="toggleCoordinates"
        >
          <v-icon :color="showCoordinates ? 'white' : 'grey darken-2'"
            >mdi-crosshairs-question</v-icon
          >
        </v-btn>

        <!-- Measure Toggle -->
        <v-btn
          fab
          small
          :color="measureMode ? 'secondary' : 'white'"
          class="mb-2"
          @click="toggleMeasure"
        >
          <v-icon :color="measureMode ? 'white' : 'grey darken-2'"
            >mdi-ruler</v-icon
          >
        </v-btn>

        <!-- Edit Mode Toggle -->
        <v-btn
          v-if="canEdit"
          fab
          small
          :color="editMode ? 'warning' : 'white'"
          class="mb-2"
          @click="editMode = !editMode"
        >
          <v-icon :color="editMode ? 'white' : 'grey darken-2'">{{
            editMode ? "mdi-pencil-off" : "mdi-pencil"
          }}</v-icon>
        </v-btn>

        <v-btn v-if="editMode" fab small color="primary" @click="openAddDialog">
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </div>

      <!-- Coordinates Display -->
      <div class="map-coordinates" v-if="currentCoords">
        <div class="caption font-weight-bold">
          WGS84: {{ currentCoords.lat.toFixed(5) }},
          {{ currentCoords.lng.toFixed(5) }}
        </div>
        <div class="caption font-weight-bold">
          {{ formatToDm(currentCoords.lat) }} N,
          {{ formatToDm(currentCoords.lng) }} E
        </div>
        <div class="caption">
          ETRS-TM35FIN: N {{ currentCoords.n.toFixed(0) }} E
          {{ currentCoords.e.toFixed(0) }}
        </div>
      </div>

      <!-- Weather Widget -->
      <div class="map-weather" v-if="weather">
        <div
          class="d-flex justify-space-between align-center"
          style="min-width: 100px"
        >
          <div class="caption font-weight-bold" v-if="!weatherMinimized">
            Sää
          </div>
          <div class="caption font-weight-bold d-flex align-center" v-else>
            <v-icon
              small
              :style="{
                transform: `rotate(${weather.windDirection + 180}deg)`,
              }"
              color="primary"
              class="mr-1"
              >mdi-arrow-up-thick</v-icon
            >
            {{ weather.temperature }}°C
          </div>
          <v-btn icon x-small @click="weatherMinimized = !weatherMinimized">
            <v-icon>{{
              weatherMinimized ? "mdi-chevron-down" : "mdi-chevron-up"
            }}</v-icon>
          </v-btn>
        </div>
        <v-expand-transition>
          <div v-show="!weatherMinimized">
            <div class="weather-item" title="Tuulen suunta ja nopeus (puuska)">
              <v-icon
                :style="{
                  transform: `rotate(${weather.windDirection + 180}deg)`,
                }"
                color="primary"
                >mdi-arrow-up-thick</v-icon
              >
              <span class="font-weight-bold ml-1">
                {{ weather.windSpeed }} m/s
                <span
                  v-if="weather.windGust"
                  class="grey--text text--darken-1"
                  style="font-size: 0.9em"
                >
                  ({{ weather.windGust }})
                </span>
              </span>
            </div>
            <div class="weather-item" title="Lämpötila">
              <v-icon color="red">mdi-thermometer</v-icon>
              <span class="font-weight-bold ml-1"
                >{{ weather.temperature }}°C</span
              >
            </div>
            <div
              class="weather-item"
              v-if="weather.fwi !== null && weather.fwi !== undefined"
              title="Metsäpaloindeksi (FWI)"
            >
              <v-icon color="orange">mdi-fire</v-icon>
              <span class="font-weight-bold ml-1">FWI: {{ weather.fwi }}</span>
            </div>
          </div>
        </v-expand-transition>
      </div>

      <!-- Measurement Display -->
      <div class="map-measurements" v-if="measureMode && !measureDialog">
        <div class="d-flex justify-space-between align-center">
          <div class="caption font-weight-bold">Mittaus</div>
          <div>
            <v-btn
              icon
              x-small
              @click="measureMinimized = !measureMinimized"
              class="mr-1"
            >
              <v-icon>{{
                measureMinimized ? "mdi-chevron-up" : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
            <v-btn icon x-small @click="measureDialog = true">
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </div>
        </div>
        <v-expand-transition>
          <div v-show="!measureMinimized">
            <div v-if="measurePoints.length > 0">
              <div class="caption">
                Matka: {{ (measureDistance / 1000).toFixed(2) }} km
              </div>
              <div class="caption" v-if="measurePoints.length > 2">
                Pinta-ala: {{ (measureArea / 10000).toFixed(2) }} ha
              </div>
            </div>

            <div class="d-flex flex-wrap mt-1" style="gap: 4px">
              <v-btn
                x-small
                color="primary"
                @click="addCurrentLocationToMeasure"
                title="Lisää nykyinen sijainti"
              >
                <v-icon x-small>mdi-crosshairs-gps</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="primary"
                @click="addMapCenterToMeasure"
                title="Lisää kartan keskipiste"
              >
                <v-icon x-small>mdi-map-marker-plus</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="warning"
                @click="undoMeasurePoint"
                :disabled="measurePoints.length === 0"
                title="Kumoa"
              >
                <v-icon x-small>mdi-undo</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="warning"
                @click="redoMeasurePoint"
                :disabled="redoStack.length === 0"
                title="Tee uudelleen"
              >
                <v-icon x-small>mdi-redo</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="success"
                @click="saveMeasurePoints"
                :disabled="measurePoints.length === 0"
                title="Tallenna laitteelle"
              >
                <v-icon x-small>mdi-content-save</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="info"
                @click="loadSavedMeasurePoints"
                title="Lataa tallennettu"
              >
                <v-icon x-small>mdi-download</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="secondary"
                @click="copyMeasurementLink"
                :disabled="measurePoints.length === 0"
                title="Jaa linkki"
              >
                <v-icon x-small>mdi-link</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="secondary"
                @click="copyMeasurementText"
                :disabled="measurePoints.length === 0"
                title="Kopioi tiedot (SMS)"
              >
                <v-icon x-small>mdi-clipboard-text</v-icon>
              </v-btn>
              <v-btn
                x-small
                :color="showMeasureLabels ? 'primary' : 'secondary'"
                @click="showMeasureLabels = !showMeasureLabels"
                title="Näytä/piilota koordinaatit kartalla"
              >
                <v-icon x-small>mdi-label</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="secondary"
                @click="takeScreenshot"
                title="Ota kuvakaappaus"
              >
                <v-icon x-small>mdi-camera</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="error"
                @click="clearMeasure"
                :disabled="measurePoints.length === 0"
                title="Tyhjennä"
              >
                <v-icon x-small>mdi-delete</v-icon>
              </v-btn>
            </div>
          </div>
        </v-expand-transition>
      </div>
    </div>

    <!-- Search Dialog -->
    <v-dialog v-model="searchDialog" max-width="400">
      <v-card>
        <v-card-title>Haku</v-card-title>
        <v-card-text>
          <v-btn-toggle
            v-model="searchMode"
            mandatory
            class="mb-4"
            style="width: 100%"
          >
            <v-btn value="address" width="50%">
              <v-icon left>mdi-map-marker</v-icon> Osoite
            </v-btn>
            <v-btn value="coordinates" width="50%">
              <v-icon left>mdi-crosshairs-gps</v-icon> Koordinaatit
            </v-btn>
          </v-btn-toggle>

          <v-text-field
            v-model="searchQuery"
            :label="searchLabel"
            @keyup.enter="performSearchMobile"
            prepend-inner-icon="mdi-magnify"
            clearable
            autofocus
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="searchDialog = false">Sulje</v-btn>
          <v-btn
            color="primary"
            @click="performSearchMobile"
            :loading="searching"
            >Hae</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Marker Dialog -->
    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>{{
          isEditing
            ? "Muokkaa merkintää"
            : newMarker.isLocal
            ? "Lisää oma merkintä"
            : "Lisää uusi merkintä"
        }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="newMarker.name"
              label="Nimi"
              required
              :rules="[(v) => !!v || 'Nimi vaaditaan']"
            ></v-text-field>
            <v-textarea
              v-model="newMarker.description"
              label="Kuvaus"
              rows="2"
            ></v-textarea>
            <v-select
              v-model="newMarker.type"
              :items="markerTypes"
              label="Tyyppi"
              required
              :rules="[(v) => !!v || 'Tyyppi vaaditaan']"
            ></v-select>

            <file-upload
              v-if="!newMarker.isLocal"
              ref="markerUpload"
              :multiple="false"
              :max-files="1"
              accepted-file-types="image/*"
              label="Lisää kuva"
              @update:files="onFilePondUpdate($event)"
            ></file-upload>

            <input
              type="file"
              accept="image/*"
              capture="environment"
              style="display: none"
              ref="cameraInput"
              @change="onCameraCapture"
            />
            <v-btn
              v-if="!newMarker.isLocal"
              outlined
              small
              class="mb-3"
              @click="$refs.cameraInput.click()"
            >
              <v-icon left small>mdi-camera</v-icon>
              Ota kuva kameralla
            </v-btn>

            <v-img
              v-if="cameraPreviewUrl && markerFiles.length > 0"
              :src="cameraPreviewUrl"
              max-width="100%"
              max-height="200"
              contain
              class="mb-4"
            ></v-img>

            <v-img
              v-if="
                newMarker.imageUrl &&
                markerFiles.length === 0 &&
                !cameraPreviewUrl
              "
              :src="newMarker.imageUrl"
              max-width="100%"
              max-height="200"
              contain
              class="mb-4"
            ></v-img>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  v-model.number="newMarker.lat"
                  label="Leveysaste (Lat)"
                  type="number"
                  step="any"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="newMarker.lng"
                  label="Pituusaste (Lng)"
                  type="number"
                  step="any"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Peruuta</v-btn>
          <v-btn
            color="primary"
            @click="saveMarker"
            :disabled="!valid"
            :loading="saving"
            >Tallenna</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image View Dialog -->
    <v-dialog v-model="imageDialog" max-width="90%" max-height="90%">
      <v-card>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn icon @click="imageDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-actions>
        <v-img
          v-if="selectedImageUrl"
          :src="selectedImageUrl"
          contain
          max-height="80vh"
        ></v-img>
      </v-card>
    </v-dialog>

    <!-- Measurement Tools Dialog -->
    <v-dialog v-model="measureDialog" max-width="600" scrollable>
      <v-card>
        <v-card-title class="headline">
          Mittaus
          <v-spacer></v-spacer>
          <v-btn icon @click="measureDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="measurePoints.length > 0" class="mb-4">
            <div class="subtitle-1">
              Matka:
              <strong>{{ (measureDistance / 1000).toFixed(2) }} km</strong>
            </div>
            <div class="subtitle-1" v-if="measurePoints.length > 2">
              Pinta-ala:
              <strong>{{ (measureArea / 10000).toFixed(2) }} ha</strong>
            </div>
          </div>
          <div v-else class="mb-4 grey--text">
            Ei mittauspisteitä. Klikkaa karttaa lisätäksesi pisteitä.
          </div>

          <div class="d-flex flex-wrap" style="gap: 8px">
            <v-btn
              color="primary"
              @click="addCurrentLocationToMeasure"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-crosshairs-gps</v-icon> Oma sijainti
            </v-btn>

            <v-btn
              color="primary"
              @click="addMapCenterToMeasure"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-map-marker-plus</v-icon> Kartan keskipiste
            </v-btn>

            <v-btn
              color="warning"
              @click="undoMeasurePoint"
              :disabled="measurePoints.length === 0"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-undo</v-icon> Kumoa
            </v-btn>

            <v-btn
              color="warning"
              @click="redoMeasurePoint"
              :disabled="redoStack.length === 0"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-redo</v-icon> Tee uudelleen
            </v-btn>

            <v-btn
              color="success"
              @click="saveMeasurePoints"
              :disabled="measurePoints.length === 0"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-content-save</v-icon> Tallenna
            </v-btn>

            <v-btn
              color="info"
              @click="loadSavedMeasurePoints"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-download</v-icon> Lataa
            </v-btn>

            <v-btn
              color="secondary"
              @click="copyMeasurementLink"
              :disabled="measurePoints.length === 0"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-link</v-icon> Jaa linkki
            </v-btn>

            <v-btn
              color="secondary"
              @click="copyMeasurementText"
              :disabled="measurePoints.length === 0"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-clipboard-text</v-icon> Kopioi tiedot
            </v-btn>

            <v-btn
              :color="showMeasureLabels ? 'primary' : 'secondary'"
              @click="showMeasureLabels = !showMeasureLabels"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-label</v-icon> Koordinaatit
            </v-btn>

            <v-btn
              color="secondary"
              @click="takeScreenshot"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-camera</v-icon> Kuvakaappaus
            </v-btn>

            <v-btn
              color="error"
              @click="clearMeasure"
              :disabled="measurePoints.length === 0"
              class="flex-grow-1"
              small
            >
              <v-icon left small>mdi-delete</v-icon> Tyhjennä
            </v-btn>
          </div>

          <v-divider class="my-4"></v-divider>

          <v-select
            v-if="measurePoints.length > 0"
            v-model="measurementUnit"
            :items="measurementUnitOptions"
            label="Koordinaattijärjestelmä"
            dense
            outlined
            hide-details
            class="mb-3"
          ></v-select>

          <div v-if="measurePoints.length > 0">
            <div class="subtitle-2 mb-2">Pisteet:</div>
            <div
              v-for="(point, index) in measurePoints"
              :key="index"
              class="caption d-flex align-center justify-space-between mb-1"
            >
              <span>{{ formatPoint(point) }}</span>
              <v-btn
                icon
                x-small
                color="error"
                @click="removeMeasurePoint(index)"
                title="Poista piste"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="measureDialog = false">Sulje</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Save Measurement Dialog -->
    <v-dialog v-model="saveMeasureDialog" max-width="400">
      <v-card>
        <v-card-title>Tallenna mittaus</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="measurementName"
            label="Nimi"
            autofocus
            @keyup.enter="confirmSaveMeasurement"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="saveMeasureDialog = false">Peruuta</v-btn>
          <v-btn color="primary" @click="confirmSaveMeasurement"
            >Tallenna</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Load Measurement Dialog -->
    <v-dialog v-model="loadMeasureDialog" max-width="500">
      <v-card>
        <v-card-title>Lataa mittaus</v-card-title>
        <v-card-text>
          <v-list v-if="savedMeasurements.length > 0">
            <v-list-item
              v-for="item in savedMeasurements"
              :key="item.id"
              two-line
            >
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ formatMeasurementDate(item.date) }} -
                  {{ item.points.length }} pistettä
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <div class="d-flex">
                  <v-btn icon color="primary" @click="loadMeasurement(item)">
                    <v-icon>mdi-upload</v-icon>
                  </v-btn>
                  <v-btn icon color="error" @click="deleteMeasurement(item.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <div v-else class="text-center pa-4 grey--text">
            Ei tallennettuja mittauksia
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="loadMeasureDialog = false">Sulje</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </basic-page>
</template>

<script>
import BasicPage from "@/components/BasicPage";
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LControlLayers,
  LPolyline,
  LPolygon,
  LCircleMarker,
  LCircle,
  LTooltip,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  setDoc,
} from "firebase/firestore";
import { firestore as db, storage } from "@/plugins/firebase.app";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import dayjs from "dayjs";
import FileUpload from "@/components/FileUpload";
import proj4 from "proj4";
import { SimpleMapScreenshoter } from "leaflet-simple-map-screenshoter";

// Define ETRS-TM35FIN
proj4.defs(
  "EPSG:3067",
  "+proj=utm +zone=35 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
);

const createEmptyMarker = () => ({
  id: null,
  name: "",
  description: "",
  type: "other",
  lat: 0,
  lng: 0,
  imageUrl: null,
  isLocal: false,
});

// Fix default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "MapView",
  components: {
    BasicPage,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LControlLayers,
    LPolyline,
    LPolygon,
    LCircleMarker,
    LCircle,
    LTooltip,
    FileUpload,
  },
  data() {
    return {
      editMode: false,
      tempEditAccess: false,
      zoom: 13,
      center: [61.592, 21.875], // Noormarkku center approx
      userLocation: null,
      userAccuracy: 0,
      watchId: null,
      locating: false,
      showFilters: false,
      searchDialog: false,
      dialog: false,
      imageDialog: false,
      saveMeasureDialog: false,
      loadMeasureDialog: false,
      measureDialog: false,
      measurementName: "",
      savedMeasurements: [],
      selectedImageUrl: null,
      valid: false,
      saving: false,
      activeFilters: [
        "fire_hydrant",
        "water_station",
        "tap_water",
        "natural_water",
        "hazard",
        "meeting",
        "boat_launch",
        "other",
        "alarm",
        "local", // Add local filter
      ],
      markers: [],
      localMarkers: [], // Store local markers
      sharedMarkers: [], // Store markers from URL
      newMarker: createEmptyMarker(),
      editingMarkerId: null,
      latestAlarmMarker: null,
      searchQuery: "",
      searchMarker: null,
      searching: false,
      currentCoords: null,
      showCoordinates: false,
      measureMode: false,
      measureMinimized: false,
      showMeasureLabels: true,
      measurePoints: [],
      redoStack: [],
      screenshoter: null,
      tempClickMarker: null,
      weather: null,
      weatherMinimized: false,
      weatherMarker: null,
      weatherTimeout: null,
      loadingWindGrid: false,
      windGridMarkers: [],
      markerFiles: [],
      cameraPreviewUrl: null,
      searchMode: "address",
      measurementUnit: localStorage.getItem("measurementUnit") || "wgs84_dec", // wgs84_dec, wgs84_dm, etrs
      measurementUnitOptions: [
        { text: "WGS84 (desimaali)", value: "wgs84_dec" },
        { text: "WGS84 (aste/min)", value: "wgs84_dm" },
        { text: "ETRS-TM35FIN", value: "etrs" },
      ],
      markerTypes: [
        { text: "Paloposti", value: "fire_hydrant" },
        { text: "Vesiasema", value: "water_station" },
        { text: "Vesijohtovesi (paloasema tai muu)", value: "tap_water" },
        { text: "Luonnonvedenottopaikka", value: "natural_water" },
        { text: "Vaarakohde", value: "hazard" },
        { text: "Kokoontumispaikka", value: "meeting" },
        { text: "Veneenlaskupaikka", value: "boat_launch" },
        { text: "Muu", value: "other" },
      ],
      tileProviders: [
        {
          name: "OpenStreetMap",
          visible: true,
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          attribution:
            '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        },
        // MML Maastokartta removed (deprecated/unavailable). Use OpenTopoMap or other providers.
        {
          name: "Maasto (OpenTopoMap)",
          visible: false,
          url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
          attribution:
            'Map data: &copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)',
        },
        {
          name: "Satelliitti (Esri)",
          visible: false,
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          attribution:
            "Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community",
        },
      ],
    };
  },
  computed: {
    searchLabel() {
      return this.searchMode === "address"
        ? "Hae osoite"
        : "Hae koordinaatit (lat, lon / ETRS)";
    },
    canEdit() {
      return this.$isAdmin || this.tempEditAccess;
    },
    filteredMarkers() {
      if (!this.activeFilters || this.activeFilters.length === 0) return [];

      // Combine all marker sources
      const allMarkers = [
        ...this.markers,
        ...this.localMarkers.map((m) => ({
          ...m,
          isLocal: true,
          type: m.type || "other",
        })),
        ...this.sharedMarkers.map((m) => ({
          ...m,
          isShared: true,
          type: m.type || "other",
        })),
      ];

      return allMarkers.filter((m) => {
        // If it's a local or shared marker, check if 'local' filter is active
        if ((m.isLocal || m.isShared) && this.activeFilters.includes("local")) {
          return true;
        }
        // Otherwise check standard type filter
        return this.activeFilters.includes(m.type);
      });
    },
    isEditing() {
      return !!this.editingMarkerId;
    },
    measureDistance() {
      if (this.measurePoints.length < 2) return 0;
      let dist = 0;
      for (let i = 0; i < this.measurePoints.length - 1; i++) {
        dist += this.measurePoints[i].distanceTo(this.measurePoints[i + 1]);
      }
      return dist;
    },
    measureArea() {
      if (this.measurePoints.length < 3) return 0;
      const points = this.measurePoints.map((p) =>
        proj4("EPSG:4326", "EPSG:3067", [p.lng, p.lat]),
      );
      let area = 0;
      for (let i = 0; i < points.length; i++) {
        const j = (i + 1) % points.length;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
      }
      return Math.abs(area) / 2;
    },
  },
  watch: {
    measurementUnit(val) {
      localStorage.setItem("measurementUnit", val);
    },
    measurePoints(newPoints) {
      const query = { ...this.$route.query };
      if (newPoints && newPoints.length > 0) {
        const serialized = newPoints
          .map((p) => `${p.lat.toFixed(5)},${p.lng.toFixed(5)}`)
          .join(";");
        query.measure = serialized;

        // Save in-progress measurement
        try {
          const points = newPoints.map((p) => ({ lat: p.lat, lng: p.lng }));
          localStorage.setItem(
            "in_progress_measurement",
            JSON.stringify(points),
          );
        } catch (e) {
          console.error("Failed to save in-progress measurement", e);
        }
      } else {
        delete query.measure;
        // Clear in-progress measurement
        localStorage.removeItem("in_progress_measurement");
      }
      // Only update if changed
      if (query.measure !== this.$route.query.measure) {
        this.$router.replace({ query }).catch(() => {});
      }
    },
  },
  methods: {
    formatPoint(point) {
      if (!point) return "";
      switch (this.measurementUnit) {
        case "wgs84_dm":
          return `${this.formatToDm(point.lat)} N, ${this.formatToDm(
            point.lng,
          )} E`;
        case "etrs": {
          const etrs = this.getEtrsCoordinates(point.lat, point.lng);
          return `N ${etrs.n} E ${etrs.e}`;
        }
        case "wgs84_dec":
        default:
          return `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
      }
    },
    async togglePublicEdit() {
      try {
        const configRef = doc(db, "config", "map");
        await setDoc(
          configRef,
          {
            allowPublicEdit: !this.tempEditAccess,
          },
          { merge: true },
        );

        this.$toast.success(
          this.tempEditAccess
            ? "Julkinen muokkaus poistettu käytöstä"
            : "Julkinen muokkaus sallittu",
        );
      } catch (error) {
        console.error("Error toggling public edit:", error);
        this.$toast.error("Virhe oikeuksien muuttamisessa");
      }
    },
    copyAppLink() {
      const url = new URL(window.location.href);

      // Serialize local markers to URL
      if (this.localMarkers.length > 0) {
        try {
          const markersData = this.localMarkers.map(({ id, ...m }) => m); // Exclude ID
          const serialized = btoa(
            unescape(encodeURIComponent(JSON.stringify(markersData))),
          );
          url.searchParams.set("markers", serialized);
        } catch (e) {
          console.error("Failed to serialize markers", e);
        }
      }

      navigator.clipboard
        .writeText(url.toString())
        .then(() => {
          this.$toast.success("Linkki kopioitu leikepöydälle");
        })
        .catch(() => {
          this.$toast.error("Linkin kopiointi epäonnistui");
        });
    },
    openGoogleMaps() {
      if (!this.$refs.map || !this.$refs.map.mapObject) return;
      const center = this.$refs.map.mapObject.getCenter();
      const zoom = this.$refs.map.mapObject.getZoom();
      // Google Maps URL format: https://www.google.com/maps/@lat,lng,zoomz
      const url = `https://www.google.com/maps/@${center.lat},${center.lng},${zoom}z`;
      window.open(url, "_blank");
    },
    loadLocalMarkers() {
      try {
        const stored = localStorage.getItem("local_markers");
        if (stored) {
          this.localMarkers = JSON.parse(stored);
        }
      } catch (e) {
        console.error("Failed to load local markers", e);
      }
    },
    saveLocalMarkers() {
      try {
        localStorage.setItem(
          "local_markers",
          JSON.stringify(this.localMarkers),
        );
      } catch (e) {
        console.error("Failed to save local markers", e);
      }
    },
    openAddLocalDialog(coords) {
      this.resetNewMarker();
      let lat, lng;

      if (
        coords &&
        typeof coords.lat === "number" &&
        typeof coords.lng === "number"
      ) {
        lat = coords.lat;
        lng = coords.lng;
      } else {
        const center = this.$refs.map.mapObject.getCenter();
        lat = center.lat;
        lng = center.lng;
      }

      this.newMarker.lat = lat;
      this.newMarker.lng = lng;
      this.newMarker.isLocal = true; // Flag as local
      this.dialog = true;
    },
    async loadMarkers() {
      const querySnapshot = await getDocs(collection(db, "markers"));
      const markers = [];
      querySnapshot.forEach((doc) => {
        markers.push({ ...doc.data(), id: doc.id });
      });
      this.markers = markers;
    },
    onFilePondUpdate(files) {
      this.markerFiles = files;
      this.cameraPreviewUrl = null;
    },
    onCameraCapture(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.cameraPreviewUrl = URL.createObjectURL(file);
      this.markerFiles = [{ file }];
      if (this.$refs.markerUpload) {
        this.$refs.markerUpload.reset();
      }
    },
    resetNewMarker() {
      this.newMarker = createEmptyMarker();
      this.markerFiles = [];
      this.cameraPreviewUrl = null;
      if (this.$refs.cameraInput) {
        this.$refs.cameraInput.value = "";
      }
      if (this.$refs.markerUpload) {
        this.$refs.markerUpload.reset();
      }
      this.valid = false;
      if (this.$refs.form && this.$refs.form.resetValidation) {
        this.$refs.form.resetValidation();
      }
      this.editingMarkerId = null;
    },
    closeDialog() {
      this.dialog = false;
      this.resetNewMarker();
    },
    async addMarker(marker) {
      const { id, isLocal, ...rest } = marker;
      const markerToSave = {
        ...rest,
        editor: this.$userDBContext,
      };
      console.debug("Adding marker to Firestore:", markerToSave);
      await addDoc(collection(db, "markers"), markerToSave);
      this.loadMarkers();
      this.editingMarkerId = null;
    },
    async updateMarker(id, marker) {
      const { id: _id, isLocal, ...rest } = marker;
      const markerToSave = {
        ...rest,
        editor: this.$userDBContext,
      };
      console.debug("Updating marker", id, markerToSave);
      await updateDoc(doc(db, "markers", id), markerToSave);
      this.loadMarkers();
      // ensure editing state cleared immediately after successful update
      this.editingMarkerId = null;
    },
    async deleteMarker(markerId) {
      console.debug("Deleting marker", markerId);
      await deleteDoc(doc(db, "markers", markerId));
      this.markers = this.markers.filter((m) => m.id !== markerId);
    },
    async fetchLatestAlarm() {
      if (!this.$isAdmin) {
        return;
      }
      if (
        !this.$store.state.digivihot ||
        this.$store.state.digivihot.length === 0
      ) {
        await this.$store.dispatch("loadDigivihot", dayjs().year());
      }
      const alarms = this.$store.state.digivihot.filter(
        (d) => d.type === "Hälytys",
      );

      if (alarms.length > 0) {
        const latest = alarms[0];
        if (latest.address) {
          console.log("Fetching geocode for latest alarm:", latest);
          this.geocodeAndSetAlarm(latest);
        }
      }
    },
    async geocodeAndSetAlarm(alarm) {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            alarm.address,
          )}`,
        );
        const data = await response.json();
        if (data && data.length > 0) {
          this.latestAlarmMarker = {
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon),
            name: `Hälytys: ${alarm.code || ""}`,
            description: `${alarm.address} - ${alarm.subject || ""}`,
            date: alarm.date,
            time: alarm.time,
            type: "alarm",
          };
        }
      } catch (e) {
        console.error("Geocoding failed", e);
      }
    },
    locateUser() {
      if (this.watchId) {
        // Stop watching if already watching
        navigator.geolocation.clearWatch(this.watchId);
        this.watchId = null;
        this.userLocation = null;
        this.userAccuracy = 0;
        this.locating = false;
        this.$toast.info("Paikannus lopetettu");
        return;
      }

      this.locating = true;
      if (!navigator.geolocation) {
        this.$toast.error("Selain ei tue paikannusta");
        this.locating = false;
        return;
      }

      // Timeout after 15 seconds if no accurate position found
      setTimeout(() => {
        if (this.locating) {
          this.locating = false;
          if (this.watchId) {
            navigator.geolocation.clearWatch(this.watchId);
            this.watchId = null;
          }
          this.$toast.error("Sijaintia ei löytynyt (aikakatkaisu)");
        }
      }, 15000);

      this.watchId = navigator.geolocation.watchPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const accuracy = position.coords.accuracy;

          this.userLocation = [lat, lng];
          this.userAccuracy = accuracy;

          // Only pan/zoom on first accurate fix or if we were waiting for it
          if (this.locating) {
            if (accuracy < 1000) {
              // Wait for reasonable accuracy (< 1km)
              this.center = [lat, lng];
              this.zoom = 15;
              this.locating = false;
              this.$toast.success(
                `Sijainti löytyi (tarkkuus: ${Math.round(accuracy)}m)`,
              );
            }
          }
        },
        (error) => {
          console.error(error);
          if (this.locating) {
            this.$toast.error("Paikannus epäonnistui");
            this.locating = false;
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    },
    toggleMeasure() {
      this.measureMode = !this.measureMode;
      // Do not clear measurePoints when toggling off, so they persist in memory/localStorage
      // if (!this.measureMode) {
      //   this.measurePoints = [];
      // }
    },
    toggleCoordinates() {
      this.showCoordinates = !this.showCoordinates;
      if (!this.showCoordinates) {
        this.currentCoords = null;
      }
    },
    clearMeasure() {
      this.measurePoints = [];
      this.redoStack = [];
    },
    undoMeasurePoint() {
      const point = this.measurePoints.pop();
      if (point) {
        this.redoStack.push(point);
      }
    },
    redoMeasurePoint() {
      const point = this.redoStack.pop();
      if (point) {
        this.measurePoints.push(point);
      }
    },
    openSaveMeasureDialog() {
      this.measurementName = `Mittaus ${dayjs().format("DD.MM.YYYY HH:mm")}`;
      this.saveMeasureDialog = true;
    },
    confirmSaveMeasurement() {
      if (!this.measurementName) {
        this.$toast.error("Anna mittaukselle nimi");
        return;
      }
      try {
        const points = this.measurePoints.map((p) => ({
          lat: p.lat,
          lng: p.lng,
        }));

        const newMeasurement = {
          id: Date.now().toString(),
          name: this.measurementName,
          points: points,
          date: new Date().toISOString(),
        };

        const stored = localStorage.getItem("saved_measurements_list");
        let list = stored ? JSON.parse(stored) : [];
        list.push(newMeasurement);

        localStorage.setItem("saved_measurements_list", JSON.stringify(list));
        this.$toast.success("Mittaus tallennettu");
        this.saveMeasureDialog = false;
      } catch (e) {
        console.error("Failed to save measurement", e);
        this.$toast.error("Tallennus epäonnistui");
      }
    },
    openLoadMeasureDialog() {
      try {
        const stored = localStorage.getItem("saved_measurements_list");
        if (stored) {
          this.savedMeasurements = JSON.parse(stored);
          // Sort by date desc
          this.savedMeasurements.sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          );
        } else {
          this.savedMeasurements = [];
        }
        this.loadMeasureDialog = true;
      } catch (e) {
        console.error("Failed to load measurements list", e);
        this.$toast.error("Lataus epäonnistui");
      }
    },
    loadMeasurement(measurement) {
      try {
        this.measurePoints = measurement.points.map((p) =>
          L.latLng(p.lat, p.lng),
        );
        this.measureMode = true;
        this.redoStack = [];
        this.loadMeasureDialog = false;
        this.$toast.success(`Ladattu "${measurement.name}"`);

        // Center map
        if (
          this.measurePoints.length > 0 &&
          this.$refs.map &&
          this.$refs.map.mapObject
        ) {
          const bounds = L.latLngBounds(this.measurePoints);
          this.$refs.map.mapObject.fitBounds(bounds, { padding: [50, 50] });
        }
      } catch (e) {
        console.error("Failed to load measurement", e);
        this.$toast.error("Lataus epäonnistui");
      }
    },
    deleteMeasurement(id) {
      if (!confirm("Haluatko varmasti poistaa tämän mittauksen?")) return;

      try {
        this.savedMeasurements = this.savedMeasurements.filter(
          (m) => m.id !== id,
        );
        localStorage.setItem(
          "saved_measurements_list",
          JSON.stringify(this.savedMeasurements),
        );
        this.$toast.success("Mittaus poistettu");
      } catch (e) {
        console.error("Failed to delete measurement", e);
        this.$toast.error("Poisto epäonnistui");
      }
    },
    saveMeasurePoints() {
      // Legacy single save - redirect to new dialog
      this.openSaveMeasureDialog();
    },
    loadSavedMeasurePoints() {
      // Legacy single load - redirect to new dialog
      this.openLoadMeasureDialog();
    },
    addMapCenterToMeasure() {
      if (this.$refs.map && this.$refs.map.mapObject) {
        const center = this.$refs.map.mapObject.getCenter();
        const point = L.latLng(center.lat, center.lng);
        this.measurePoints.push(point);
        this.redoStack = [];
        this.$toast.success("Keskipiste lisätty mittaukseen");
      }
    },
    removeMeasurePoint(index) {
      this.measurePoints.splice(index, 1);
      this.redoStack = []; // Clear redo stack to avoid inconsistencies
    },
    addCurrentLocationToMeasure() {
      if (!navigator.geolocation) {
        this.$toast.error("Selain ei tue paikannusta");
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          const point = L.latLng(lat, lng);
          this.measurePoints.push(point);
          this.redoStack = [];
          this.$toast.success("Sijainti lisätty mittaukseen");
        },
        (error) => {
          console.error(error);
          this.$toast.error("Sijainnin haku epäonnistui");
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 },
      );
    },
    copyMeasurementLink() {
      // Ensure URL is up to date
      const url = window.location.href;
      navigator.clipboard.writeText(url).then(() => {
        this.$toast.success("Linkki kopioitu leikepöydälle");
      });
    },
    copyMeasurementText() {
      if (this.measurePoints.length === 0) return;

      let text = "Mittauspisteet:\n";
      this.measurePoints.forEach((p, i) => {
        text += `${p.lat.toFixed(5)}, ${p.lng.toFixed(5)}\n`;
      });

      if (this.measurePoints.length > 1) {
        text += `\nMatka: ${(this.measureDistance / 1000).toFixed(2)} km`;
      }
      if (this.measurePoints.length > 2) {
        text += `\nPinta-ala: ${(this.measureArea / 10000).toFixed(2)} ha`;
      }

      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success("Tiedot kopioitu leikepöydälle");
      });
    },
    takeScreenshot() {
      if (!this.screenshoter) {
        this.$toast.error("Kuvakaappaus ei ole käytettävissä");
        return;
      }

      const isMobile = window.innerWidth < 600;
      const fontSize = isMobile ? 12 : 16;

      let caption = "";

      if (isMobile) {
        // Short format for mobile
        caption = `${dayjs().format("DD.MM HH:mm")}`;
        if (this.measurePoints.length > 1) {
          caption += ` | ${(this.measureDistance / 1000).toFixed(2)} km`;
        }
        if (this.measurePoints.length > 2) {
          caption += ` | ${(this.measureArea / 10000).toFixed(2)} ha`;
        }
      } else {
        // Full format for desktop
        caption = `Mittaus ${dayjs().format("DD.MM.YYYY HH:mm")}`;
        if (this.measurePoints.length > 1) {
          caption += ` | Matka: ${(this.measureDistance / 1000).toFixed(2)} km`;
        }
        if (this.measurePoints.length > 2) {
          caption += ` | Pinta-ala: ${(this.measureArea / 10000).toFixed(
            2,
          )} ha`;
        }
      }

      this.screenshoter
        .takeScreen("image", {
          caption: caption,
          captionFontSize: fontSize,
          captionFont: "Arial",
          captionColor: "black",
          captionBgColor: "white",
          captionOffset: 5,
          mimeType: "image/png",
        })
        .then((image) => {
          // Create a link to download the image
          const link = document.createElement("a");
          link.download = `kartta_mittaus_${dayjs().format(
            "DD-MM-YYYY_HH-mm",
          )}.png`;
          link.href = image;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          this.$toast.success("Kuvakaappaus tallennettu");
        })
        .catch((e) => {
          console.error("Screenshot failed", e);
          this.$toast.error("Kuvakaappaus epäonnistui");
        });
    },
    getEtrsCoordinates(lat, lng) {
      try {
        const [easting, northing] = proj4("EPSG:4326", "EPSG:3067", [lng, lat]);
        return { n: Math.round(northing), e: Math.round(easting) };
      } catch (e) {
        return { n: 0, e: 0 };
      }
    },
    formatToDm(val) {
      const deg = Math.floor(Math.abs(val));
      const min = (Math.abs(val) - deg) * 60;
      return `${deg}° ${min.toFixed(3)}'`;
    },
    copyCoordinates(lat, lng) {
      const etrs = this.getEtrsCoordinates(lat, lng);
      const latDm = this.formatToDm(lat);
      const lngDm = this.formatToDm(lng);
      const text = `WGS84: ${lat.toFixed(5)}, ${lng.toFixed(
        5,
      )}\nWGS84 (DM): ${latDm} N, ${lngDm} E\nETRS-TM35FIN: N ${etrs.n} E ${
        etrs.e
      }`;
      navigator.clipboard.writeText(text).then(() => {
        this.$toast.success("Koordinaatit kopioitu leikepöydälle");
      });
    },
    onMouseMove(e) {
      if (!this.showCoordinates) return;
      const { lat, lng } = e.latlng;
      const etrs = this.getEtrsCoordinates(lat, lng);
      this.currentCoords = {
        lat,
        lng,
        e: etrs.e,
        n: etrs.n,
      };
    },
    onMapClick(e) {
      if (this.measureMode) {
        this.measurePoints.push(e.latlng);
        this.redoStack = [];
        return;
      }
      if (this.editMode) {
        this.resetNewMarker();
        this.newMarker.lat = e.latlng.lat;
        this.newMarker.lng = e.latlng.lng;
        this.dialog = true;
      } else {
        this.tempClickMarker = { lat: e.latlng.lat, lng: e.latlng.lng };
      }
    },
    openAddDialog() {
      this.resetNewMarker();
      // Use map center for new marker
      const center = this.$refs.map.mapObject.getCenter();
      this.newMarker.lat = center.lat;
      this.newMarker.lng = center.lng;
      this.dialog = true;
    },
    openEditDialog(marker) {
      this.editingMarkerId = marker.id;
      this.newMarker = {
        name: marker.name || "",
        description: marker.description || "",
        type: marker.type || "other",
        lat: marker.lat,
        lng: marker.lng,
        imageUrl: marker.imageUrl || null,
        isLocal: marker.isLocal || false, // Preserve local flag
      };
      this.dialog = true;
    },
    openImage(url) {
      this.selectedImageUrl = url;
      this.imageDialog = true;
    },
    updateTempMarkerPosition(latLng) {
      this.newMarker.lat = latLng.lat;
      this.newMarker.lng = latLng.lng;
    },
    async saveMarker() {
      if (!this.$refs.form.validate()) return;
      this.saving = true;
      try {
        if (this.newMarker.isLocal) {
          // Handle local marker save
          const markerToSave = {
            ...this.newMarker,
            id: this.editingMarkerId || Date.now().toString(),
            updatedAt: new Date().toISOString(),
          };

          if (this.editingMarkerId) {
            const index = this.localMarkers.findIndex(
              (m) => m.id === this.editingMarkerId,
            );
            if (index !== -1) {
              this.localMarkers.splice(index, 1, markerToSave);
            }
          } else {
            this.localMarkers.push(markerToSave);
          }

          this.saveLocalMarkers();
          this.$toast.success("Oma merkintä tallennettu");
          this.closeDialog();
          this.saving = false;
          return;
        }

        // Handle image upload
        if (this.markerFiles.length > 0) {
          const file = this.markerFiles[0].file;
          const storageRef = ref(storage, `markers/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          this.newMarker.imageUrl = await getDownloadURL(storageRef);
        }

        if (this.editingMarkerId) {
          await this.updateMarker(this.editingMarkerId, this.newMarker);
        } else {
          await this.addMarker(this.newMarker);
        }
        this.$toast.success("Merkintä tallennettu");
        this.closeDialog();
      } catch (e) {
        console.error(e);
        this.$toast.error("Tallennus epäonnistui");
      } finally {
        this.saving = false;
        // safety: clear editing id so later saves cannot accidentally add duplicates
        this.editingMarkerId = null;
      }
    },
    async deleteMarkerConfirm(marker) {
      if (!confirm(`Haluatko varmasti poistaa merkinnän "${marker.name}"?`))
        return;

      if (marker.isLocal) {
        this.localMarkers = this.localMarkers.filter((m) => m.id !== marker.id);
        this.saveLocalMarkers();
        this.$toast.success("Merkintä poistettu");
        return;
      }

      try {
        await this.deleteMarker(marker.id);
        this.$toast.success("Merkintä poistettu");
      } catch (e) {
        console.error(e);
        this.$toast.error("Poisto epäonnistui");
      }
    },
    getMarkerIcon(markerOrType) {
      // Handle both string type (legacy calls) and marker object
      const type =
        typeof markerOrType === "object" ? markerOrType.type : markerOrType;
      const isLocal = typeof markerOrType === "object" && markerOrType.isLocal;
      const isShared =
        typeof markerOrType === "object" && markerOrType.isShared;

      const icons = {
        hydrant: { icon: "mdi-fire-hydrant", color: "blue" }, // legacy
        fire_hydrant: { icon: "mdi-fire-hydrant", color: "blue" },
        water_station: { icon: "mdi-water-pump", color: "light-blue" },
        tap_water: { icon: "mdi-faucet", color: "teal" },
        natural_water: { icon: "mdi-waves", color: "cyan" },
        hazard: { icon: "mdi-alert", color: "red" },
        meeting: { icon: "mdi-account-group", color: "green" },
        boat_launch: { icon: "mdi-ferry", color: "cyan" },
        alarm: { icon: "mdi-bell-ring", color: "red pulsing" },
        search: { icon: "mdi-magnify", color: "orange" },
        user: { icon: "mdi-account", color: "indigo" },
        other: { icon: "mdi-map-marker", color: "grey" },
      };
      const config = icons[type] || icons.other;

      let className = "custom-div-icon";
      // Add a small dot/badge for local markers
      const badgeHtml = isLocal ? '<div class="local-marker-badge"></div>' : "";

      let html = `<div class="marker-pin ${config.color} ${
        isLocal ? "local-pin" : ""
      } ${isShared ? "shared-pin" : ""}">
        <i class="v-icon mdi ${config.icon}"></i>
        ${badgeHtml}
      </div>`;

      return L.divIcon({
        className: className,
        html: html,
        iconSize: [30, 42],
        iconAnchor: [15, 42],
        popupAnchor: [0, -32],
      });
    },
    getWindMarkerIcon(rotation) {
      // Rotate 180 degrees to point downwind (direction wind is blowing TO)
      return L.divIcon({
        className: "wind-marker-icon",
        html: `<div style="transform: rotate(${
          rotation + 180
        }deg); width: 100%; height: 100%; display: flex; justify-content: center; align-items: center;">
                 <i class="v-icon mdi mdi-arrow-up-thick" style="font-size: 50px; color: #1976D2; text-shadow: 0 0 5px white; opacity: 0.9;"></i>
               </div>`,
        iconSize: [50, 50],
        iconAnchor: [25, 25],
      });
    },
    getTypeLabel(type) {
      const t = this.markerTypes.find((t) => t.value === type);
      return t ? t.text : type;
    },
    getTypeColor(type) {
      switch (type) {
        case "hydrant":
        case "fire_hydrant":
          return "blue";
        case "water_station":
          return "light-blue";
        case "tap_water":
          return "teal";
        case "natural_water":
          return "cyan";
        case "hazard":
          return "red";
        case "meeting":
          return "green";
        case "boat_launch":
          return "cyan";
        case "alarm":
          return "red";
        default:
          return "grey";
      }
    },
    formatDate(date) {
      if (!date) return "";
      const parts = date.split("-");
      if (parts.length !== 3) return date;
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    },
    formatMeasurementDate(date) {
      if (!date) return "";
      return dayjs(date).format("DD.MM.YYYY HH:mm");
    },
    async performSearchMobile() {
      const success = await this.performSearch();
      if (success) {
        this.searchDialog = false;
      }
    },
    async performSearch() {
      if (this.searchMode === "address") {
        return await this.searchAddress();
      } else {
        return await this.searchCoordinates();
      }
    },
    async searchCoordinates() {
      const query = this.searchQuery.trim();
      if (!query) {
        this.$toast.error("Syötä koordinaatit");
        return false;
      }

      this.searching = true;
      try {
        let lat, lng;

        // Normalize: replace commas with spaces, remove N/E, trim
        // "N 6800000 E 300000" -> "6800000 300000"
        let clean = query
          .toUpperCase()
          .replace(/[N|E]/g, "")
          .replace(/,/g, " ")
          .trim();

        // Split by whitespace
        const parts = clean.split(/\s+/).map((p) => parseFloat(p));

        if (parts.length < 2 || parts.some(isNaN)) {
          this.$toast.error("Tarkista koordinaattimuoto");
          this.searching = false;
          return false;
        }

        const [c1, c2] = parts;

        // Heuristic: ETRS usually > 1000. WGS84 Lat < 90.
        // Also supports reversed order detection if needed, but standard is N, E or Lat, Lon
        if (c1 > 90 || c2 > 180) {
          // ETRS-TM35FIN
          // Assume Order: N, E (Northing, Easting) because that's how they are usually written/spoken
          // Proj4 takes [x, y] -> [Easting, Northing]
          // So x = E = c2, y = N = c1

          const wgs84 = proj4("EPSG:3067", "EPSG:4326", [c2, c1]);
          lng = wgs84[0];
          lat = wgs84[1];
        } else {
          // WGS84 Decimal
          // Assume Lat, Lon order
          lat = c1;
          lng = c2;
        }

        // Simple bounds check
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          this.center = [lat, lng];
          this.zoom = 15;
          this.searchMarker = {
            lat,
            lng,
            address: `Koordinaatit: ${lat.toFixed(5)}, ${lng.toFixed(5)}`,
          };
          this.$toast.success("Koordinaatit löytyi");
          return true;
        } else {
          this.$toast.error("Virheelliset koordinaatit");
          return false;
        }
      } catch (e) {
        console.error(e);
        this.$toast.error("Virhe koordinaattien käsittelyssä");
        return false;
      } finally {
        this.searching = false;
      }
    },
    async searchAddress() {
      if (!this.searchQuery.trim()) {
        this.$toast.error("Syötä osoite");
        return false;
      }
      this.searching = true;
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            this.searchQuery,
          )}&limit=1`,
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const result = data[0];
          const lat = parseFloat(result.lat);
          const lng = parseFloat(result.lon);
          this.center = [lat, lng];
          this.zoom = 15;
          this.searchMarker = {
            lat,
            lng,
            address: result.display_name,
          };
          // Auto-clear after 30 seconds
          setTimeout(() => {
            this.searchMarker = null;
          }, 30000);
          this.$toast.success("Osoite löytyi");
          return true;
        } else {
          this.$toast.error("Osoitetta ei löytynyt");
          return false;
        }
      } catch (e) {
        console.error("Geocoding failed", e);
        this.$toast.error("Haku epäonnistui");
        return false;
      } finally {
        this.searching = false;
      }
    },
    onMapMoveEnd() {
      if (this.weatherTimeout) clearTimeout(this.weatherTimeout);
      this.weatherTimeout = setTimeout(() => {
        this.fetchWeather();
      }, 5000);

      // Update URL with current position
      if (this.$refs.map && this.$refs.map.mapObject) {
        const center = this.$refs.map.mapObject.getCenter();
        const zoom = this.$refs.map.mapObject.getZoom();

        const query = this.$route.query;
        // Only update if changed significantly to avoid loops/noise
        if (
          !query.lat ||
          !query.lng ||
          !query.zoom ||
          Math.abs(parseFloat(query.lat) - center.lat) > 0.00001 ||
          Math.abs(parseFloat(query.lng) - center.lng) > 0.00001 ||
          parseInt(query.zoom) !== zoom
        ) {
          this.$router
            .replace({
              query: {
                ...query,
                lat: center.lat.toFixed(5),
                lng: center.lng.toFixed(5),
                zoom: zoom.toString(),
              },
            })
            .catch(() => {});
        }
      }
    },
    async fetchWeather() {
      try {
        let lat, lng;
        let markerLat, markerLng;

        if (this.$refs.map && this.$refs.map.mapObject) {
          const map = this.$refs.map.mapObject;
          const center = map.getCenter();
          lat = center.lat;
          lng = center.lng;

          // Offset the marker so it doesn't block the center
          // Offset 80px right and 80px up (negative y is up)
          const point = map.latLngToContainerPoint(center);
          const offsetPoint = L.point(point.x + 80, point.y - 80);
          const offsetLatLng = map.containerPointToLatLng(offsetPoint);
          markerLat = offsetLatLng.lat;
          markerLng = offsetLatLng.lng;
        } else {
          [lat, lng] = this.center;
          markerLat = lat;
          markerLng = lng;
        }

        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m&wind_speed_unit=ms&timezone=auto`,
        );
        // Note: Open-Meteo free tier doesn't always have FWI in simple forecast,
        // but we can try to fetch it if available or use a placeholder.
        // For now, we use UV index as a proxy for "summer conditions" or just omit if not relevant.
        // Actually, let's try to get FWI from the archive/climate endpoint if needed,
        // but for simplicity and speed, we'll stick to current weather.
        // We will check if we can get FWI from a specific model or just leave it null for now.

        const data = await response.json();
        if (data && data.current) {
          this.weather = {
            temperature: data.current.temperature_2m,
            windSpeed: data.current.wind_speed_10m,
            windDirection: data.current.wind_direction_10m,
            windGust: data.current.wind_gusts_10m,
            fwi: null, // Placeholder for now as it requires more complex API call
          };
          this.weatherMarker = {
            lat: markerLat,
            lng: markerLng,
            rotation: data.current.wind_direction_10m,
          };
        }
      } catch (e) {
        console.error("Weather fetch failed", e);
      }
    },
    async fetchWindGrid() {
      if (!this.$refs.map || !this.$refs.map.mapObject) return;

      this.loadingWindGrid = true;
      try {
        const bounds = this.$refs.map.mapObject.getBounds();
        const south = bounds.getSouth();
        const north = bounds.getNorth();
        const west = bounds.getWest();
        const east = bounds.getEast();

        // Create a 3x3 grid (9 points)
        const lats = [];
        const lngs = [];

        // Calculate steps to place points inside the bounds, not on the very edge
        const latStep = (north - south) / 4;
        const lngStep = (east - west) / 4;

        for (let i = 1; i <= 3; i++) {
          for (let j = 1; j <= 3; j++) {
            lats.push(south + i * latStep);
            lngs.push(west + j * lngStep);
          }
        }

        const latStr = lats.join(",");
        const lngStr = lngs.join(",");

        const response = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latStr}&longitude=${lngStr}&current=wind_speed_10m,wind_direction_10m,wind_gusts_10m&wind_speed_unit=ms&timezone=auto`,
        );

        const data = await response.json();

        // Open-Meteo returns an array of objects if multiple coords are requested
        // OR a single object if only one coord (but we are sending 9)
        // However, the format for multiple points is a list of result objects.

        let results = [];
        if (Array.isArray(data)) {
          results = data;
        } else {
          // Fallback if for some reason it returns single object (shouldn't happen with our logic)
          results = [data];
        }

        this.windGridMarkers = results
          .map((res, index) => {
            if (!res.current) return null;
            return {
              lat: lats[index],
              lng: lngs[index],
              windSpeed: res.current.wind_speed_10m,
              windDirection: res.current.wind_direction_10m,
              windGust: res.current.wind_gusts_10m,
              rotation: res.current.wind_direction_10m,
            };
          })
          .filter((m) => m !== null);

        this.$toast.success(
          `Haettu tuulitiedot ${this.windGridMarkers.length} pisteeseen`,
        );
      } catch (e) {
        console.error("Wind grid fetch failed", e);
        this.$toast.error("Tuulitietojen haku epäonnistui");
      } finally {
        this.loadingWindGrid = false;
      }
    },
  },
  beforeDestroy() {
    if (this.watchId) {
      navigator.geolocation.clearWatch(this.watchId);
    }
  },
  mounted() {
    // Listen for temporary edit permission
    onSnapshot(doc(db, "config", "map"), (doc) => {
      if (doc.exists()) {
        this.tempEditAccess = doc.data().allowPublicEdit === true;
      }
    });

    // Initialize from URL if params exist
    const { lat, lng, zoom, measure, markers } = this.$route.query;
    if (lat && lng) {
      this.center = [parseFloat(lat), parseFloat(lng)];
    }
    if (zoom) {
      this.zoom = parseInt(zoom);
    }

    if (markers) {
      try {
        const decoded = decodeURIComponent(escape(atob(markers)));
        const parsed = JSON.parse(decoded);
        if (Array.isArray(parsed)) {
          this.sharedMarkers = parsed.map((m) => ({
            ...m,
            id: "shared_" + Math.random(),
          }));
          this.$toast.info(
            `Ladattu ${this.sharedMarkers.length} jaettua merkintää`,
          );
        }
      } catch (e) {
        console.error("Failed to parse shared markers", e);
      }
    }

    if (measure) {
      try {
        this.measurePoints = measure.split(";").map((pair) => {
          const [lat, lng] = pair.split(",");
          return L.latLng(parseFloat(lat), parseFloat(lng));
        });
        if (this.measurePoints.length > 0) {
          this.measureMode = true;
        }
      } catch (e) {
        console.error("Failed to parse measurement points", e);
      }
    } else {
      // Try to load in-progress measurement from local storage
      try {
        const stored = localStorage.getItem("in_progress_measurement");
        if (stored) {
          const points = JSON.parse(stored);
          if (Array.isArray(points) && points.length > 0) {
            this.measurePoints = points.map((p) => L.latLng(p.lat, p.lng));
            this.measureMode = true;
            this.$toast.info("Palautettu keskeneräinen mittaus");
          }
        }
      } catch (e) {
        console.error("Failed to load in-progress measurement", e);
      }
    }

    this.loadMarkers();
    this.loadLocalMarkers();
    this.fetchLatestAlarm();
    this.fetchWeather();
    // Fix map rendering issues when inside tabs/hidden containers
    setTimeout(() => {
      if (this.$refs.map && this.$refs.map.mapObject) {
        this.$refs.map.mapObject.invalidateSize();

        // Initialize screenshoter
        this.screenshoter = new SimpleMapScreenshoter({
          hidden: true, // hide 'screen' button
          mimeType: "image/png",
          hideElementsWithSelectors: [
            ".leaflet-control-container",
            ".map-controls",
            ".map-coordinates",
            ".map-weather",
            ".map-measurements",
            ".v-btn",
          ],
        }).addTo(this.$refs.map.mapObject);

        // Center map to measurements if any
        if (this.measurePoints.length > 0) {
          const bounds = L.latLngBounds(this.measurePoints);
          this.$refs.map.mapObject.fitBounds(bounds, { padding: [50, 50] });
        }
      }
    }, 100);
  },
};
</script>

<style scoped>
.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
}

.map-container {
  position: relative;
  height: calc(100vh - 210px); /* Adjust based on app bar/footer and header */
  width: 100%;
  z-index: 0;
}
.map-controls {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  z-index: 400;
}

.map-search {
  display: flex;
  align-items: center;
  gap: 5px;
}

.map-crosshair {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.map-coordinates {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(255, 255, 255, 0.9);
  padding: 5px 10px;
  border-radius: 4px;
  z-index: 400;
  pointer-events: none;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
}

.map-measurements {
  position: absolute;
  bottom: 80px;
  left: 20px;
  background: rgba(255, 255, 255, 0.9);
  padding: 5px 10px;
  border-radius: 4px;
  z-index: 400;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  max-width: calc(100% - 90px); /* Prevent overlap with right controls */
}

@media (max-width: 600px) {
  .map-measurements {
    left: 10px;
    bottom: 90px;
    max-width: calc(100% - 70px);
  }

  .map-coordinates {
    left: 10px;
    bottom: 10px;
    max-width: calc(100% - 70px);
    font-size: 0.75rem;
  }
}

.map-weather {
  position: absolute;
  top: 80px; /* Below the header/filters */
  left: 10px;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 4px;
  z-index: 400;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.weather-item {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
}

.map-filters-container {
  display: flex;
  align-items: flex-start;
}

:deep(.user-dot) {
  width: 18px;
  height: 18px;
  background-color: #4285f4;
  border: 3px solid white;
  border-radius: 50%;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.3);
  animation: pulse-dot 2s infinite;
}

@keyframes pulse-dot {
  0% {
    box-shadow: 0 0 0 0 rgba(66, 133, 244, 0.4);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(66, 133, 244, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(66, 133, 244, 0);
  }
}

:deep(.custom-div-icon) {
  background: transparent;
  border: none;
  overflow: visible;
  position: relative;
  width: 30px;
  height: 42px;
}

:deep(.custom-div-icon)::before {
  content: "";
  position: absolute;
  width: 14px;
  height: 6px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  filter: blur(1px);
  z-index: -1;
}

:deep(.marker-pin) {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  position: absolute;
  left: 0;
  top: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  color: #808080;
  background: currentColor;
}

:deep(.marker-pin)::after {
  content: "";
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 12px solid currentColor;
}

:deep(.marker-pin i) {
  color: white !important;
  font-size: 18px !important;
}

:deep(.marker-pin.blue) {
  color: #2196f3;
}
:deep(.marker-pin.red) {
  color: #f44336;
}
:deep(.marker-pin.green) {
  color: #4caf50;
}
:deep(.marker-pin.cyan) {
  color: #00bcd4;
}
:deep(.marker-pin.light-blue) {
  color: #03a9f4;
}
:deep(.marker-pin.teal) {
  color: #009688;
}
:deep(.marker-pin.indigo) {
  color: #3f51b5;
}
:deep(.marker-pin.grey) {
  color: #9e9e9e;
}

:deep(.marker-pin.orange) {
  color: #ff9800;
}

:deep(.marker-pin.pulsing) {
  animation: pulse 1.5s infinite;
}

:deep(.marker-pin.local-pin) {
  border: 2px dashed white;
  box-shadow: 0 0 0 2px purple;
}

:deep(.marker-pin.shared-pin) {
  border: 2px dotted white;
  box-shadow: 0 0 0 2px cyan;
}

:deep(.local-marker-badge) {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 12px;
  height: 12px;
  background-color: #9c27b0; /* Purple */
  border: 2px solid white;
  border-radius: 50%;
  z-index: 10;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(244, 67, 54, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(244, 67, 54, 0);
  }
}
</style>
